import { ButtonLink } from '@designsforhealth/dfh-react-components'
import { Subscribe } from '@react-rxjs/core'
import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { GetCustomerCartQuery } from '../../graphql/magento'
import useRetailPrices from '../../hooks/useRetailPrices'
import { CartState, ToggleSummaryAction } from '../../lib/cart/reducers/cart-state'
import {
  latestCartItemProducts$,
  setCartItemSku,
  useCartItemProducts,
} from '../../lib/cart/state/cart-items/cart-item-product'
import { cartTotal, formatAsCurrency } from '../../utils/priceTools'
import { PlusMinusIcon } from '../PlusMinusIcon'

const StyledEstimatedTotal = styled.div`
  text-align: end;
  max-width: 250px;
  width: 100%;
`

const StyledPlusMinusIcon = styled(PlusMinusIcon)`
  margin-left: 8px;
`

const StyledButtonContainer = styled.div`
  display: flex;
  align-self: flex-start;
  height: auto;
`

const StyledPriceContainer = styled.div`
  display: flex;
  width: 100%;
  font-size: inherit;
  justify-content: flex-end;
  margin-bottom: 5px;

  &:first-of-type {
    padding-top: 10px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }

  p:first-of-type {
    flex: 2;
  }

  p:last-of-type {
    flex: 1;
    margin-left: 10px;
  }
`

const RetailSavings = styled.p`
  color: ${({ theme }) => theme.colors.brand.red};
  font-size: inherit;
`

const RetailSubtotal = styled.p`
  color: ${({ theme }) => theme.colors.grayscale.gray40};
  font-size: inherit;
`

export interface CartSummaryToggleProps extends Pick<CartState, 'summaryVisible'> {
  cart: NonNullable<GetCustomerCartQuery['customerCart']>
  className?: string
  dispatch: React.Dispatch<ToggleSummaryAction>
}

const Savings: React.FC<{
  skus: string[]
  cart: NonNullable<GetCustomerCartQuery['customerCart']>
}> = ({ skus, cart }) => {
  const products = useCartItemProducts(skus)
  const retailSubtotal = useMemo(() => {
    if (!products || products.length === 0) {
      return 0
    }
    let total = 0
    cart.items?.map(
      (item, index) =>
        (total +=
          parseFloat(
            products[index]?.retailPrice?.amount ?? (products[index]?.price?.amount || '0'),
          ) * (item?.quantity ?? 0)),
    )

    return total
  }, [cart.items, products])

  const shouldDisplaySavings = (cart.prices?.grand_total?.value ?? 0) < retailSubtotal
  const retailSavings = retailSubtotal - (cart.prices?.grand_total?.value ?? 0)

  if (!shouldDisplaySavings) {
    return null
  }

  return (
    <>
      <StyledPriceContainer>
        <RetailSavings>Savings today:</RetailSavings>
        <RetailSavings>-{formatAsCurrency({ amount: retailSavings })}</RetailSavings>
      </StyledPriceContainer>
      <StyledPriceContainer>
        <RetailSubtotal>Retail subtotal:</RetailSubtotal>
        <RetailSubtotal>{formatAsCurrency({ amount: retailSubtotal })}</RetailSubtotal>
      </StyledPriceContainer>
    </>
  )
}

const CartSummaryToggle: React.FC<CartSummaryToggleProps> = ({
  cart,
  className,
  dispatch,
  summaryVisible,
}) => {
  const { shouldDisplayRetailPrice } = useRetailPrices()
  const skus = useMemo(
    () => cart.items?.map((item) => item?.product.sku || '').filter(Boolean) || [],
    [cart.items],
  )

  useEffect(() => {
    cart.items?.forEach((item) => {
      if (item?.product.sku) {
        setCartItemSku(item.product.sku)
      }
    })
  }, [cart.items])

  return (
    <div className={className}>
      <StyledButtonContainer>
        <ButtonLink onClick={() => dispatch({ type: 'toggleSummary' })}>
          {summaryVisible ? 'Hide' : 'Show'} summary
          <StyledPlusMinusIcon minus={summaryVisible} />
        </ButtonLink>
      </StyledButtonContainer>
      <StyledEstimatedTotal>
        <StyledPriceContainer>
          <p>Estimated total:</p>
          <p>{cartTotal({ cart })}</p>
        </StyledPriceContainer>
        {shouldDisplayRetailPrice && skus?.length > 0 && (
          <Subscribe source$={latestCartItemProducts$(skus)} fallback={null}>
            <Savings skus={skus} cart={cart} />
          </Subscribe>
        )}
      </StyledEstimatedTotal>
    </div>
  )
}

const StyledCartSummaryToggle = styled(CartSummaryToggle)`
  display: flex;
  font-size: 14px;
  line-height: 16px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
  width: 100%;

  p {
    line-height: inherit;
    font-size: inherit;
    font-family: Gotham, sans-serif;
    margin: 0;
  }

  &,
  ${ButtonLink} {
    font-weight: 500;
  }

  ${ButtonLink} {
    // increase touch target size
    padding: 10px 0;
    margin-right: 16px;
    white-space: nowrap;
  }
`

export default StyledCartSummaryToggle
